import { api } from 'app/api';
import { IValidateSignupLinkQuery, IValidateSignupLinkResponse } from 'modules/signup/types';
import { toLoginResult } from './loginResult';
import {
  IChangePasswordMutation,
  ICheckAuthMethodMutation,
  ICheckAuthMethodResponse,
  ILoginMutation,
  IMFAVerifyMutation,
  IResetPasswordMutation,
  ISAMLCompleteMutation,
  ISSOLoginMutation,
  TLoginResult,
} from './types';

const authApi = api.injectEndpoints({
  endpoints: (build) => ({
    login: build.mutation<TLoginResult, ILoginMutation>({
      query: (payload) => ({
        url: `/auth/login/`,
        method: 'POST',
        body: payload,
      }),
      transformResponse: (body: unknown, meta) => toLoginResult(body, meta),
    }),
    verifyMFA: build.mutation<void, IMFAVerifyMutation>({
      query: (payload) => ({
        url: `/auth/mfa/verify/`,
        method: 'POST',
        body: payload,
      }),
    }),
    nautilusLogin: build.mutation<null, ILoginMutation>({
      query: (payload) => ({
        url: `/auth/nautilus-login/`,
        method: 'POST',
        body: payload,
      }),
    }),
    logout: build.mutation<null, null>({
      query: () => ({
        url: `/auth/logout/`,
        method: 'POST',
      }),
      invalidatesTags: ['user-info'],
    }),
    checkAuthMethod: build.mutation<ICheckAuthMethodResponse, ICheckAuthMethodMutation>({
      query: (payload) => ({
        url: `/auth/method/`,
        method: 'GET',
        params: payload,
      }),
    }),
    loginWithSSO: build.mutation<void, ISSOLoginMutation>({
      query: (payload) => ({
        url: `/auth/sso/`,
        method: 'POST',
        body: payload,
      }),
      // A refused sign-in signed nobody in, so there is nothing to re-read.
      // Tags are invalidated on a rejected mutation as well as a successful
      // one, and re-reading the user is what empties the whole app while the
      // answer is in flight: the callback page was torn down and rebuilt on
      // every refusal, posting the spent authorization code again each time.
      invalidatesTags: (result, error) => (error ? [] : ['user-info']),
    }),
    completeSAMLLogin: build.mutation<void, ISAMLCompleteMutation>({
      query: (payload) => ({
        url: `/auth/saml/complete/`,
        method: 'POST',
        body: payload,
      }),
      invalidatesTags: (result, error) => (error ? [] : ['user-info']),
    }),
    resetConfirmEmail: build.mutation<undefined, { email: string }>({
      query: (payload) => ({
        url: '/auth/password/reset/',
        method: 'POST',
        body: payload,
      }),
    }),
    resetPassword: build.mutation<undefined, IResetPasswordMutation>({
      query: (payload) => ({
        url: '/auth/password/reset/confirm/',
        method: 'POST',
        body: payload,
      }),
    }),
    changePassword: build.mutation<void, IChangePasswordMutation>({
      query: (payload) => ({
        url: '/auth/password/change/',
        method: 'POST',
        body: payload,
      }),
    }),
    validateSignupLink: build.query<IValidateSignupLinkResponse, IValidateSignupLinkQuery>({
      query: (queryParams) => ({
        url: `/validate-signup-link/`,
        params: { ...queryParams },
        method: 'GET',
      }),
    }),
  }),
});

export const {
  useLoginMutation,
  useVerifyMFAMutation,
  useNautilusLoginMutation,
  useLogoutMutation,
  useResetConfirmEmailMutation,
  useResetPasswordMutation,
  useChangePasswordMutation,
  useLoginWithSSOMutation,
  useCompleteSAMLLoginMutation,
  useCheckAuthMethodMutation,
  useValidateSignupLinkQuery,
} = authApi;
export default authApi;
